import { NavLink, Outlet } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Wordmark } from '@/domain/wordmark';
import { MicroLabel } from '@/ui/micro-label';
import { useProfile } from '@/app/session';
import { RealtimeProvider } from '@/lib/realtime';
import { ErrorBoundary } from '@/app/error-boundary';
import { SignOutDialog } from './sign-out';

const NAV = [
  { label: 'Dashboard', to: '/admin' },
  { label: 'Vendors', to: '/admin/vendors' },
  { label: 'Engagements', to: '/admin/engagements' },
  { label: 'Datasets', to: '/admin/datasets' },
  { label: 'Data Factory', to: '/admin/data-factory' },
  { label: 'Delivery', to: '/admin/delivery' },
  { label: 'Settings', to: '/admin/settings' }
];

/**
 * Operator console chrome: fixed sidebar with the account pinned to the bottom.
 * The active item carries a 2px accent bar on its left edge plus a raised background.
 */
export function InternalShell() {
  const { data: profile } = useProfile('admin');

  return (
    <RealtimeProvider realm="admin" enabled={!!profile}>
      <div className="grid min-h-screen grid-cols-[220px_1fr] bg-background max-lg:grid-cols-1">
        <aside className="flex flex-col gap-6 border-r border-border bg-chrome px-4 py-5 max-lg:hidden">
          <NavLink to="/admin" className="px-2">
            <Wordmark height={13} />
          </NavLink>
          <nav className="grid gap-0.5">
            <MicroLabel size="xs" className="mb-2 px-2">
              Platform
            </MicroLabel>
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/admin'}
                className={({ isActive }) =>
                  cn(
                    'relative rounded px-3 py-2 text-[13px] transition-colors',
                    isActive
                      ? 'bg-raised text-foreground before:absolute before:top-1.5 before:bottom-1.5 before:-left-1 before:w-0.5 before:rounded-full before:bg-primary before:content-[""]'
                      : 'text-muted hover:text-foreground'
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <SignOutDialog
            realm="admin"
            profile={profile}
            trigger={
              <button
                type="button"
                title="Account"
                className="mt-auto flex cursor-pointer items-center gap-3 rounded border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-border-strong"
              >
                <span className="size-6 shrink-0 rounded-full bg-primary" />
                <span className="grid gap-0.5">
                  <span className="text-[13px] text-foreground">{profile?.name ?? 'Account'}</span>
                  <MicroLabel size="xs">{profile?.role ?? 'operator'}</MicroLabel>
                </span>
              </button>
            }
          />
        </aside>
        <main className="w-full px-8 py-6 max-lg:px-5">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </RealtimeProvider>
  );
}
